/**
 * VibeCats — Package Section (Frame Animation)
 *
 * Sistema reutilizável de animação por sequência de frames (spritesheet-like)
 * controlado via GSAP ScrollTrigger.
 *
 * Uso:
 *   1. Coloque as imagens da sequência em dist/images/frames/
 *      Nomeie como frame001.webp, frame002.webp, ..., frame180.webp
 *   2. A animação será acionada conforme o usuário scrolla pela seção.
 */

const PackageAnim = (($) => {
    'use strict';

    // Configuração da animação de frames
    const config = {
        canvasId: 'package-frame-canvas',
        frameCount: 301,
        framesDir: '',  // Será preenchido com themeUri
        framePrefix: 'frame_',
        frameExt: '.webp',
        digits: 4,       // zero-padding: 001, 002, ...
    };

    let canvas = null;
    let ctx = null;
    let images = [];
    let loadedCount = 0;
    let currentFrame = 0;
    let tl = null;
    let isLoaded = false;

    /**
     * Gera o nome do arquivo para o frame index.
     * @param {number} index — 1-based
     * @returns {string}
     */
    function getFrameFilename(index) {
        const padded = String(index).padStart(config.digits, '0');
        return `${config.framePrefix}${padded}${config.frameExt}`;
    }

    /**
     * Carrega todas as imagens da sequência.
     * @returns {Promise<void>}
     */
    function loadFrames() {
        return new Promise((resolve) => {
            images = [];
            loadedCount = 0;

            for (let i = 1; i <= config.frameCount; i++) {
                const img = new Image();
                const filename = getFrameFilename(i);
                img.src = `${config.framesDir}${filename}`;

                img.onload = () => {
                    loadedCount++;
                    if (loadedCount === config.frameCount) {
                        isLoaded = true;
                        resolve();
                    }
                };

                img.onerror = () => {
                    // Se alguma imagem falhar, ainda resolve para não quebrar
                    loadedCount++;
                    if (loadedCount === config.frameCount) {
                        isLoaded = true;
                        resolve();
                    }
                };

                images.push(img);
            }
        });
    }

    /**
     * Desenha o frame atual no canvas.
     * @param {number} index — índice da imagem (0-based)
     */
    function drawFrame(index) {

        if (!ctx || !canvas) return;

        const img = images[index];

        if (!img || !img.complete) return;

        const dpr = window.devicePixelRatio || 1;

        const cw = canvas.width / dpr;
        const ch = canvas.height / dpr;

        ctx.clearRect(0, 0, cw, ch);

        // Escala mantendo toda a imagem visível
        const scale = Math.min(
            cw / img.width,
            ch / img.height
        );

        const drawWidth = img.width * scale;
        const drawHeight = img.height * scale;

        const x = (cw - drawWidth) * 0.5;
        const y = (ch - drawHeight) * 0.5;

        ctx.drawImage(
            img,
            x,
            y,
            drawWidth,
            drawHeight
        );
    }

    /**
     * Inicializa o canvas e prepara a cena.
     */
    function initCanvas() {
        canvas = document.getElementById(config.canvasId);

        if (!canvas) {
            //console.warn('[PackageAnim] Canvas não encontrado:', config.canvasId);
            return false;
        }

        ctx = canvas.getContext('2d');

        function resizeCanvas() {

            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;

            canvas.width = Math.round(rect.width * dpr);
            canvas.height = Math.round(rect.height * dpr);

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            drawFrame(currentFrame);
        }

        resizeCanvas();

        const resize = Utils.debounce(() => {
            resizeCanvas();

            // Recalcula posições do ScrollTrigger
            ScrollTrigger.refresh();
        }, 150);

        window.addEventListener('resize', resize);

        Animations.addCleanup(() => {
            window.removeEventListener('resize', resize);
        });

        return true;
    }

    /**
     * Cria a ScrollTrigger que controla a sequência de frames.
     */
    function createScrollAnimation() {
        if (!canvas || !isLoaded) return;

        const packageEl = document.querySelector('#package');

        if (!packageEl) return;

        gsap.set(packageEl, {
            opacity: 0
        });
        

        tl = $.timeline({
            scrollTrigger: {
                trigger: '#package',
                start: 'top 40%',
                end: '90% 90%',
                scrub: 1.5,
                pin: false,
                //markers: true,
                invalidateOnRefresh: true,
            },
            onUpdate: () => {
                if (!tl) return;
                // Mapeia o progresso da timeline (0-1) para o frame
                const progress = tl.progress();
                const frameIndex = Math.round(progress * (config.frameCount - 1));
                if (frameIndex !== currentFrame) {
                    currentFrame = frameIndex;
                    drawFrame(frameIndex);
                }
            },
        });

        tl.to(packageEl, {
            opacity: 1,
            duration: 0.2,
            ease: "none"
        });

        // Adiciona um tween vazio para ter duração
        tl.to({}, { duration: 1 });
    }

    /**
     * Cria animações complementares para o conteúdo da seção Package.
     */
    function createContentAnimations() {
        // Conteúdo aparece ao scroll
        const packageInfo = document.querySelector('[data-anim="package-info"]');
        if (packageInfo) {
            $.from(packageInfo, {
                scrollTrigger: {
                    trigger: '#package',
                    start: 'top 80%',
                    end: 'center 60%',
                    toggleActions: 'play none none reverse',
                },
                y: 60,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
            });
        }
    }

    /**
     * Inicializa o módulo.
     * @param {string} themeUri — URL base do tema
     */
    async function init(themeUri) {
        config.framesDir = `${themeUri}/dist/images/frames/`;

        if (Utils.prefersReducedMotion()) {
            // Apenas mostra o primeiro frame
            if (initCanvas()) {
                await loadFrames();
                drawFrame(0);
            }
            return;
        }

        const canvasReady = initCanvas();
        if (!canvasReady) return;

        // Carrega frames
        await loadFrames();

        // Se carregou ao menos uma imagem, mostra o frame inicial
        if (images.length > 0) {
            drawFrame(0);
        }

        // Cria animação de scroll
        createScrollAnimation();

        // Animações complementares
        createContentAnimations();
    }

    /**
     * Destroi as animações.
     */
    function destroy() {
        if (tl) {
            tl.kill();
            tl = null;
        }
        images = [];
        isLoaded = false;
        loadedCount = 0;
    }

    return {
        init,
        destroy,
        get config() { return config; },
    };
})(gsap);
